/**
 * Store Locator — frontend bootstrap.
 *
 * The store list is rendered immediately from /ale-hop/v1/stores and is fully
 * usable on its own. The Google Map is *lazy-loaded*: `new google.maps.Map`
 * (the billable Dynamic Maps load) is only instantiated when the user shows
 * intent to use the map — clicking the map facade, opening the "Map" tab on
 * mobile, selecting a store from the list, or using geolocation. Users who only
 * browse the list never trigger a map load.
 */

import { gsap } from "gsap";

const GMAPS_BASE = "https://maps.googleapis.com/maps/api/js";

/**
 * Canonical key for a region label: trimmed, lowercased, accent-stripped.
 * Lets the dropdown collapse dirty data ("A coruña" / "A Coruña", " Aveiro")
 * into a single entry and the filter match case/accent-insensitively, even on
 * environments whose stored data hasn't been normalized yet.
 */
function regionKey(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

let gmapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (gmapsPromise) return gmapsPromise;
  gmapsPromise = new Promise((resolve, reject) => {
    const cb = "__alehopGmapsReady";
    window[cb] = () => resolve(window.google.maps);
    const s = document.createElement("script");
    s.src = `${GMAPS_BASE}?key=${encodeURIComponent(apiKey)}&callback=${cb}&loading=async&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gmapsPromise;
}

function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);
}

async function init(root) {
  const cfg = window.AleHopStoreLocator;
  const i18n = cfg?.i18n || {};
  const countryLabels = i18n.countryLabels || {};
  const countryLabel = (code) => countryLabels[code] || code || "";
  if (!cfg?.apiKey) {
    root.innerHTML = `<p style="padding:1rem;color:#a00">${escapeHtml(
      i18n.apiKeyMissing ||
        "Falta la API key de Google Maps en Tiendas → Ajustes.",
    )}</p>`;
    return;
  }

  const els = {
    map: root.querySelector("[data-sl-map]"),
    facade: root.querySelector("[data-sl-facade]"),
    facadeImg: root.querySelector("[data-sl-facade-img]"),
    list: root.querySelector("[data-sl-list]"),
    count: root.querySelector("[data-sl-count]"),
    empty: root.querySelector("[data-sl-empty]"),
    country: root.querySelector("[data-sl-country]"),
    region: root.querySelector("[data-sl-region]"),
    geo: root.querySelector("[data-sl-geo]"),
    tpl: root.querySelector("[data-sl-item-template]"),
    tabs: root.querySelectorAll("[data-sl-tab]"),
    panels: root.querySelectorAll("[data-sl-panel]"),
  };

  // --- Map state (created lazily on first activation) --------------------
  let maps = null;
  let map = null;
  let infoWindow = null;
  let clusterer = null;
  let mapActivated = false;
  let activating = null;
  const markersById = new Map();
  const itemsById = new Map();
  let activeId = null;

  const stores = await fetch(cfg.restUrl, {
    credentials: "same-origin",
    cache: "no-store",
  }).then((r) => r.json());

  if (!Array.isArray(stores) || stores.length === 0) {
    els.empty.hidden = false;
    if (els.facade) els.facade.hidden = true;
    return;
  }

  // Paint the static facade preview (if the backend provided a URL). Without
  // it — or if it fails to load (e.g. the Static Maps SKU isn't authorized on
  // the key) — hide the broken image so only the clean CTA panel shows.
  if (els.facadeImg && cfg.staticMapUrl) {
    els.facadeImg.addEventListener("error", () => {
      els.facadeImg.hidden = true;
    });
    els.facadeImg.src = cfg.staticMapUrl;
  }

  /**
   * Hand the stage over from the static facade to the live map: the pre-render
   * pushes in slightly while it cross-fades into the interactive map underneath.
   * The scale-up hides the seam between the two (the facade is a lower-resolution
   * render of the same area, so a hard swap reads as a glitch) and only runs on
   * the way out, once the pre-render — Google's attribution included — has been
   * fully visible. Honours prefers-reduced-motion with a plain swap.
   */
  function dismissFacade() {
    if (!els.facade || els.facade.hidden) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      els.facade.hidden = true;
      return;
    }

    gsap
      .timeline({
        onComplete: () => {
          els.facade.hidden = true;
          // Drop the inline styles so a later re-show (mobile tab switching
          // before activation) isn't stuck at opacity 0 / scaled up.
          gsap.set([els.facade, els.facadeImg, els.map], {
            clearProps: "all",
          });
        },
      })
      // Fade in the map div, not its wrapper — the facade lives inside the
      // wrapper, so animating that would fade the facade along with it.
      .fromTo(
        els.map,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.6, ease: "power1.out" },
        0,
      )
      .to(els.facadeImg, { scale: 1.06, duration: 0.9, ease: "power2.inOut" }, 0)
      .to(els.facade, { autoAlpha: 0, duration: 0.5, ease: "power1.out" }, 0.15);
  }

  // One dataLayer push per session when the interactive map is first activated.
  // With lazy-load, 1 activation == 1 billable Dynamic Maps load, so comparing
  // this event count against locator page_views measures the exact % of map
  // loads saved. GTM (loaded by Cookiebot) consumes window.dataLayer.
  function pushMapActivate(trigger) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "store_locator_map_activate",
      interaction_category: "store_locator",
      interaction_action: "map_activate",
      interaction_label: trigger || "unknown",
      store_locator_device: window.matchMedia("(max-width: 767px)").matches
        ? "mobile"
        : "desktop",
      store_locator_language: document.documentElement.lang || "",
    });
  }

  /**
   * Instantiate the Google Map on demand. Idempotent: concurrent or repeated
   * calls resolve to the same in-flight promise and never re-instantiate.
   * `trigger` records what prompted activation (facade / map_tab / store_item /
   * geolocation) for the analytics event.
   */
  function activateMap(trigger) {
    if (mapActivated) return Promise.resolve();
    if (activating) return activating;
    pushMapActivate(trigger);
    // Reveal the sidebar/list immediately on intent (CSS animates it on
    // desktop); don't gate the reveal on the map's network round-trip. On
    // mobile, switch to the map panel the user just chose to load.
    root.classList.add("is-map-active");
    if (window.matchMedia("(max-width: 767px)").matches) {
      selectTab("map");
    }
    activating = (async () => {
      maps = await loadGoogleMaps(cfg.apiKey);

      // Bounding box of every store — used to keep panning within the served
      // regions (so the user can't wander off to another continent).
      const allBounds = new maps.LatLngBounds();
      stores.forEach((s) => allBounds.extend({ lat: s.lat, lng: s.lng }));

      const mapOptions = {
        center: { lat: cfg.defaultLat, lng: cfg.defaultLng },
        zoom: cfg.defaultZoom,
        minZoom: cfg.minZoom || undefined,
        maxZoom: cfg.maxZoom || undefined,
        // "cooperative": normal scroll pans the PAGE (no hijack); zoom needs
        // Ctrl/⌘ + wheel on desktop and two fingers on mobile.
        gestureHandling: "cooperative",
        restriction: { latLngBounds: allBounds, strictBounds: false },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      };
      // AdvancedMarkerElement requires a cloud-based Map ID (which then drives
      // the styling); without one, fall back to the classic Marker + JSON style.
      if (cfg.mapId) {
        mapOptions.mapId = cfg.mapId;
      } else {
        mapOptions.styles = cfg.mapStyle;
      }
      map = new maps.Map(els.map, mapOptions);
      infoWindow = new maps.InfoWindow();

      let AdvancedMarkerElement = null;
      if (cfg.mapId) {
        try {
          ({ AdvancedMarkerElement } = await maps.importLibrary("marker"));
        } catch {
          AdvancedMarkerElement = null;
        }
      }

      // Legacy Marker needs an icon spec; AdvancedMarkerElement uses DOM content.
      const icon = AdvancedMarkerElement
        ? null
        : {
            url: cfg.markerUrl,
            scaledSize: new maps.Size(36, 36),
            anchor: new maps.Point(18, 18),
          };
      for (const store of stores) {
        const position = { lat: store.lat, lng: store.lng };
        let marker;
        if (AdvancedMarkerElement) {
          const img = document.createElement("img");
          img.src = cfg.markerUrl;
          img.width = 36;
          img.height = 36;
          img.alt = "";
          img.style.transform = "translateY(50%)"; // center the icon on the point
          marker = new AdvancedMarkerElement({
            position,
            title: store.name,
            content: img,
            gmpClickable: true,
          });
        } else {
          marker = new maps.Marker({ position, title: store.name, icon });
        }
        marker.addListener("click", () => focusStore(store, { zoom: 15 }));
        markersById.set(store.id, marker);
      }

      // Self-hosted (bundled via Vite), loaded on demand — no third-party CDN.
      const { MarkerClusterer } = await import("@googlemaps/markerclusterer");
      const clustererOptions = { map, markers: [] };
      if (AdvancedMarkerElement) {
        // Render cluster bubbles as AdvancedMarkerElements too, so no part of
        // the map falls back to the deprecated google.maps.Marker.
        clustererOptions.renderer = {
          render: ({ count, position }) => {
            const el = document.createElement("div");
            el.className = "sl-cluster";
            el.textContent = String(count);
            return new AdvancedMarkerElement({ position, content: el });
          },
        };
      }
      clusterer = new MarkerClusterer(clustererOptions);

      mapActivated = true;
      dismissFacade();

      // Re-render the in-view markers whenever the map settles (pan/zoom), so
      // we never push all ~343 pins to the clusterer at once.
      map.addListener("idle", renderMarkers);

      // Initial framing: keep the configured (tight) center/zoom unless a
      // filter is active, then fit to that subset.
      if (els.country.value || els.region.value) {
        fitToVisible();
      }
      renderMarkers();
    })();
    return activating;
  }

  function setActive(id, opts = {}) {
    if (activeId === id && !opts.force) return;
    if (activeId !== null) {
      itemsById.get(activeId)?.classList.remove("is-active");
    }
    activeId = id;
    const item = itemsById.get(id);
    if (item) {
      item.classList.add("is-active");
      if (opts.scroll) item.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  async function focusStore(store, { zoom = 15 } = {}) {
    await activateMap("store_item");
    map.panTo({ lat: store.lat, lng: store.lng });
    map.setZoom(zoom);
    const marker = markersById.get(store.id);
    if (marker) {
      infoWindow.setContent(
        `<strong>${escapeHtml(store.name)}</strong><br>${escapeHtml(store.address || "")}, ${escapeHtml(store.postcode || "")} ${escapeHtml(store.city || "")}`,
      );
      infoWindow.open({ anchor: marker, map });
    }
    setActive(store.id, { scroll: true });
  }

  function renderList(visibleStores) {
    els.list.innerHTML = "";
    itemsById.clear();
    if (visibleStores.length === 0) {
      els.empty.hidden = false;
      els.count.textContent = "";
      return;
    }
    els.empty.hidden = true;
    const noun =
      visibleStores.length === 1
        ? i18n.storeSingular || "tienda"
        : i18n.storePlural || "tiendas";
    els.count.textContent = `${visibleStores.length} ${noun}`;

    const frag = document.createDocumentFragment();
    for (const store of visibleStores) {
      const node = els.tpl.content.firstElementChild.cloneNode(true);
      node.dataset.id = store.id;
      node.querySelector("[data-sl-name]").textContent = store.name;
      node.querySelector("[data-sl-address]").textContent = store.address || "";
      node.querySelector("[data-sl-postcode]").textContent = store.postcode || "";
      node.querySelector("[data-sl-city]").textContent = store.city || "";
      node.querySelector("[data-sl-region-label]").textContent = store.region || "";
      node.querySelector("[data-sl-country-label]").textContent = countryLabel(
        store.country,
      );
      node.querySelector("[data-sl-phone]").textContent = store.phone || "";
      const gmapsLink = node.querySelector("[data-sl-gmaps]");
      if (store.gmaps) {
        gmapsLink.href = store.gmaps;
        gmapsLink.addEventListener("click", (e) => e.stopPropagation());
      } else {
        gmapsLink.remove();
      }
      const activate = () => {
        focusStore(store, { zoom: 15 });
        const mapTab = root.querySelector('[data-sl-tab="map"]');
        if (mapTab && window.matchMedia("(max-width: 767px)").matches) {
          mapTab.click();
        }
      };
      node.addEventListener("click", activate);
      node.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      });
      node.querySelector("[data-sl-view]").addEventListener("click", (e) => {
        e.stopPropagation();
        activate();
      });
      els.list.appendChild(node);
      itemsById.set(store.id, node);
    }
    els.list.appendChild(frag);
  }

  function fitToVisible() {
    if (!map) return;
    const visible = currentFiltered();
    if (visible.length === 0) return;
    const bounds = new maps.LatLngBounds();
    visible.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    map.fitBounds(bounds, 40);
  }

  function currentFiltered() {
    const country = els.country.value;
    const regionK = regionKey(els.region.value);
    return stores.filter((s) => {
      if (country && s.country !== country) return false;
      if (regionK && regionKey(s.region) !== regionK) return false;
      return true;
    });
  }

  // Stores within the current viewport (padded by a margin so pins near the
  // edge appear before they scroll fully into view), intersected with the
  // active country/region filter.
  function markersInView() {
    const filtered = currentFiltered();
    if (!map) return filtered;
    const b = map.getBounds();
    if (!b) return filtered;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    const latPad = (ne.lat() - sw.lat()) * 0.25;
    const lngPad = (ne.lng() - sw.lng()) * 0.25;
    const padded = new maps.LatLngBounds(
      { lat: sw.lat() - latPad, lng: sw.lng() - lngPad },
      { lat: ne.lat() + latPad, lng: ne.lng() + lngPad },
    );
    return filtered.filter((s) => padded.contains({ lat: s.lat, lng: s.lng }));
  }

  // Feed the clusterer only the markers currently in view. No-op until the map
  // has been activated (markers/clusterer don't exist yet).
  function renderMarkers() {
    if (!mapActivated) return;
    const ids = new Set(markersInView().map((s) => s.id));
    const shown = [];
    markersById.forEach((m, id) => {
      if (ids.has(id)) shown.push(m);
    });
    clusterer.clearMarkers();
    clusterer.addMarkers(shown);
  }

  function applyFilter() {
    const visible = currentFiltered();
    renderList(visible);
    if (mapActivated) {
      if (visible.length > 0) fitToVisible();
      renderMarkers();
    }
  }

  const countries = [...new Set(stores.map((s) => s.country).filter(Boolean))].sort();
  for (const c of countries) {
    const opt = document.createElement("option");
    opt.value = c;
    opt.textContent = countryLabel(c);
    els.country.appendChild(opt);
  }

  // Pick the "richest" spelling among variants that share a canonical key:
  // prefer an uppercase initial and accented characters over bare ones.
  function regionScore(label) {
    const t = label.trim();
    const upper = /^\p{Lu}/u.test(t) ? 10 : 0;
    const accents = (t.normalize("NFD").match(/\p{Diacritic}/gu) || []).length;
    return upper + accents;
  }

  function repopulateRegions() {
    const country = els.country.value;
    els.region.innerHTML = `<option value="">${i18n.region || ""}</option>`;
    if (!country) {
      els.region.disabled = true;
      return;
    }
    const byKey = new Map();
    for (const s of stores) {
      if (s.country !== country || !s.region) continue;
      const key = regionKey(s.region);
      if (!key) continue;
      const label = s.region.trim();
      const current = byKey.get(key);
      if (!current || regionScore(label) > regionScore(current)) {
        byKey.set(key, label);
      }
    }
    const regions = [...byKey.values()].sort((a, b) => a.localeCompare(b, "es"));
    for (const r of regions) {
      const opt = document.createElement("option");
      opt.value = r;
      opt.textContent = r;
      els.region.appendChild(opt);
    }
    els.region.disabled = false;
  }

  // --- Activation triggers ------------------------------------------------

  function selectTab(target) {
    els.tabs.forEach((b) => {
      const on = b.dataset.slTab === target;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    });
    els.panels.forEach((p) =>
      p.classList.toggle("is-active", p.dataset.slPanel === target),
    );
  }

  els.tabs.forEach((btn) => {
    btn.addEventListener("click", async () => {
      const target = btn.dataset.slTab;
      if (target === "map") await activateMap("map_tab");
      selectTab(target);
      if (target === "map" && map) {
        google.maps.event.trigger(map, "resize");
        fitToVisible();
      }
    });
  });

  if (els.facade) {
    els.facade.addEventListener("click", () => activateMap("facade"));
  }

  els.country.addEventListener("change", () => {
    repopulateRegions();
    applyFilter();
  });
  els.region.addEventListener("change", applyFilter);

  els.geo.addEventListener("click", () => {
    if (!navigator.geolocation) return;
    els.geo.disabled = true;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        els.geo.disabled = false;
        const userPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        await activateMap("geolocation");
        map.panTo(userPos);
        map.setZoom(11);
        const visible = currentFiltered()
          .map((s) => ({ ...s, _d: haversineKm(userPos, { lat: s.lat, lng: s.lng }) }))
          .sort((a, b) => a._d - b._d);
        renderList(visible);
      },
      () => {
        els.geo.disabled = false;
        alert(cfg.i18n.geoDenied);
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  });

  renderList(stores);
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-store-locator]").forEach(init);
});
